import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Typography, CircularProgress, Container, Divider } from "@mui/material";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import ListImage from "../../../components/listdetails/ListImage";
import Reviews from "../../../components/serviceListings/Reviews";
import ListDetailsCard from "../../../components/listdetails/ListDetailsCard";
import AboutPolicy from "../../../components/listdetails/AboutPolicy";
import ListMedia from "../../../components/listdetails/ListMedia";
import axios from "axios";
import config from "../../../config";
import * as Actions from "../../../redux/actions/";
import { connect } from "react-redux";

const ListDetail = ({ loading, loadListings, productListings, classes, jwtToken }) => {
  const navigate = useNavigate();
  const { listId } = useParams();
  const [listData, setListData] = useState();
  const [reviews, setReviews] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [moreListings, setMoreListings] = useState([]);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  const { scrollY } = useScroll();
  const imageParallax = useTransform(scrollY, [0, 500], [0, -30]);

  // Detect sticky "stuck" state for the glow effect
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [listData]);

  useEffect(() => {
    const getListingData = async (id) => {
      try {
        const res = await axios.get(`${config.SERVER_URL}/api/v1/animals/${id}`, {
          headers: { "Content-Type": "application/json" },
        });
        if (res.status === 200) {
          setListData(res.data.data.listing);
          loadListings({ categoryId: res.data.data.listing.category_id });
          window.scrollTo({ top: 0 });
        }
      } catch (e) {}
    };
    if (listId) getListingData(listId);
  }, [listId]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(
          `${config.SERVER_URL}/api/v1/reviews?seller_id=${listData.seller_id}`,
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` } }
        );
        if (res.status === 200) setReviews(res.data.reviews);
      } catch (e) {}
    };
    if (listData) fetchReviews();
  }, [listData]);

  useEffect(() => {
    if (productListings) {
      setMoreListings(productListings.filter((i) => i.id !== listId));
      setFeaturedListings(productListings.filter((i) => i.is_featured && i.id !== listId));
    }
  }, [productListings]);

  const handleShowAllListings = () => {
    if (listData?.category_id) navigate("/listings/classes/" + listData.category_id);
  };

  const similarListings = featuredListings.length > 0 ? featuredListings.slice(0, 6) : moreListings.slice(0, 6);

  if (loading && !listData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", backgroundColor: "#111" }}>
        <CircularProgress sx={{ color: "#FFC001" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
      {/* Breadcrumb header */}
      <Box sx={{ borderBottom: "1px solid #1e1e1e", backgroundColor: "#0d0d0d", py: 1.5 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
            <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
            <Typography component={Link} to="/listings" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Listings</Typography>
            {listData?.category && (
              <>
                <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
                <Typography component={Link} to={`/classes/${listData.category_id}/breeds`} sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>
                  {listData.category.name}
                </Typography>
              </>
            )}
            {listData?.breed && (
              <>
                <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
                <Typography component={Link} to={`/listings/breed/${listData.breed_id}`} sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>
                  {listData.breed.name}
                </Typography>
              </>
            )}
            {listData && (
              <>
                <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
                <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }} noWrap>{listData.name}</Typography>
              </>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        {!listData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#FFC001" }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={4} alignItems="flex-start">

              {/* Left: Image gallery */}
              <Grid item xs={12} md={7}>
                <Box ref={sentinelRef} sx={{ height: "1px", mt: "-1px" }} />
                <motion.div
                  key={listId + "-img"}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                >
                  <Box
                    className="gallery-wrapper"
                    sx={{
                      position: { md: "sticky" },
                      top: { md: "80px" },
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor: isStuck ? "rgba(255,192,1,0.3)" : "#1e1e1e",
                      boxShadow: isStuck
                        ? "0 0 40px rgba(255,192,1,0.08), 0 4px 24px rgba(0,0,0,0.5)"
                        : "none",
                      transition: "border-color 0.5s ease, box-shadow 0.5s ease",
                      overflow: "hidden",
                    }}
                  >
                    <ListImage listData={[...listData.media, listData.ped]} imageParallax={imageParallax} />
                  </Box>
                </motion.div>
              </Grid>

              {/* Right: Info panel */}
              <Grid item xs={12} md={5}>
                <motion.div
                  key={listId + "-info"}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut", delay: 0.1 }}
                >
                  <ListDetailsCard listData={listData} />
                </motion.div>
              </Grid>
            </Grid>

            {/* About / Policy */}
            <Box sx={{ mt: 5 }}>
              <Divider sx={{ borderColor: "#222", mb: 4 }} />
              <AboutPolicy listData={listData} />
            </Box>

            {/* Reviews */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ borderColor: "#222", mb: 4 }} />
              <Reviews reviews={reviews} listData={listData} reviewType="animal" />
            </Box>

            {/* Similar listings */}
            {similarListings.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ borderColor: "#222", mb: 4 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "20px", md: "26px" } }}>
                      {featuredListings.length > 0 ? "Featured" : "Similar"}{" "}
                      <Box component="span" sx={{ color: "#FFC001" }}>Listings</Box>
                    </Typography>
                    <Typography sx={{ color: "#666", fontSize: "13px", mt: 0.3 }}>More from this category</Typography>
                  </Box>
                  <Box
                    onClick={handleShowAllListings}
                    sx={{ cursor: "pointer", backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "13px", px: 2.5, py: 0.8, borderRadius: "20px", "&:hover": { backgroundColor: "#e6ac00" } }}
                  >
                    See All
                  </Box>
                </Box>
                <ListMedia listings={similarListings} classes={classes} />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Listings, Categories, Auth }) => ({
  productListings: Listings.listings,
  loading: Listings.loading,
  classes: Categories.classes,
  jwtToken: Auth.jwtToken,
});

const mapDispatchToProps = (dispatch) => ({
  loadListings: ({ categoryId }) => dispatch(Actions.thunkLoadListing({ categoryId })),
});

export default connect(mapStateToProps, mapDispatchToProps)(ListDetail);
